import type { PublishTemplateBody } from '@reach/shared-types/agent-components';
import type { AgentTemplate } from '@reach/shared-types/agent-templates';
import { AgentTemplateKind } from '@reach/shared-types/agents';
import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../hooks/useToast';
import {
  addTemplateComponent,
  fetchTemplateComponents,
  fetchTemplateKindsUsingComponent,
  fetchTemplateTenantCount,
  fetchUnpublishedChanges,
  publishTemplate,
  removeTemplateComponent,
  templateComponentKeys,
  templateKindsUsingComponentKeys,
  templateTenantCountKeys,
  unpublishedChangesKeys,
} from './agent-template-api';
import { STANDARD_CACHE } from './useAgentComponents';

export const findVoiceTemplateId = (templates: AgentTemplate[] | undefined): string | undefined =>
  templates?.find(template => template.kind === AgentTemplateKind.VOICE)?.id;

export function useTemplateComponents(templateId: string | undefined) {
  return useQuery({
    queryKey: templateComponentKeys.list(templateId ?? ''),
    queryFn: templateId ? () => fetchTemplateComponents(templateId) : skipToken,
    ...STANDARD_CACHE,
  });
}

export function useTemplateTenantCount(templateId: string) {
  return useQuery({
    queryKey: templateTenantCountKeys.detail(templateId),
    queryFn: () => fetchTemplateTenantCount(templateId),
    ...STANDARD_CACHE,
  });
}

export function useVoiceTemplateUsage(componentId: string | undefined) {
  const {
    data: templateKindsUsingComponent = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: templateKindsUsingComponentKeys.detail(componentId ?? ''),
    queryFn: componentId ? () => fetchTemplateKindsUsingComponent(componentId) : skipToken,
    ...STANDARD_CACHE,
  });
  return {
    voiceTemplateUsesComponent: templateKindsUsingComponent.includes(AgentTemplateKind.VOICE),
    usageIsLoading: isLoading,
    usageCheckFailed: isError,
  };
}

export function useTemplateComponentMutations(templateId: string) {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const refreshTemplate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: templateComponentKeys.list(templateId) }),
      queryClient.invalidateQueries({ queryKey: templateKindsUsingComponentKeys.all }),
      queryClient.invalidateQueries({ queryKey: unpublishedChangesKeys.detail(templateId) }),
    ]);
  const showError = (title: string) => (error: Error) =>
    addToast({ title, description: error.message, variant: 'destructive' });

  const addComponents = useMutation({
    mutationFn: async (componentIds: string[]) => {
      for (const componentId of componentIds) {
        await addTemplateComponent(templateId, componentId);
      }
    },
    onSettled: refreshTemplate,
    onError: showError('Could not add everything to the template'),
  });
  const removeComponent = useMutation({
    mutationFn: (componentId: string) => removeTemplateComponent(templateId, componentId),
    onSuccess: refreshTemplate,
    onError: showError('Could not remove it from the template'),
  });
  return { addComponents, removeComponent };
}

export function useUnpublishedChanges(templateId: string) {
  return useQuery({
    queryKey: unpublishedChangesKeys.detail(templateId),
    queryFn: () => fetchUnpublishedChanges(templateId),
    ...STANDARD_CACHE,
    staleTime: 0,
  });
}

export function usePublishTemplate(templateId: string) {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (review: PublishTemplateBody = {}) => publishTemplate(templateId, review),
    onSuccess: async () => {
      addToast({ title: 'Template saved; agent updates queued', variant: 'success' });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: unpublishedChangesKeys.detail(templateId) }),
        queryClient.invalidateQueries({ queryKey: templateKindsUsingComponentKeys.all }),
      ]);
    },
  });
}
